import { useCallback, useEffect, useState } from 'react';
import { Form, Field } from 'react-final-form';
import { useDispatch, useSelector } from 'react-redux';
import type { MessageTranslator } from '@oneflowab/pomes';
import { replace } from 'connected-react-router';
import type { match as Match } from 'react-router';

import adminPage from '@app/hocs/admin-page';
import automationRulesReducer from '@app/reducers/entities/automation-rules';

import { T } from '@app/components/translation';
import { checkAcl } from '@flow/libs/utils/acl';
import Toggle from '@app/components/toggle';
import * as EditableForm from '@app/components/editable-form';
import TextField from '@app/components/text-field';
import { DetailsFooter } from '@app/components/details-footer';
import { LocalizedDateTime } from '@app/components/localized-date-time';

import style from './automation-rule-details.module.scss';
import { useDaysValidations } from '@flow/libs/data-validators/automation-rule-validator';

type Props = {
  message: MessageTranslator;
  match: Match;
  location: Location;
};

type AutomationRuleData = Pick<Oneflow.AutomationRule, 'days' | 'active'>;

export const AutomationRuleDetails = ({ message, match, location }: Props) => {
  const [isEditMode, setIsEditMode] = useState(false);
  const dispatch = useDispatch();
  const automationRule = useSelector((state) =>
    automationRulesReducer.getAutomationRuleSelector(state, {
      id: match.params.automationRuleId,
    }),
  );
  const updateState = useSelector((state) =>
    automationRulesReducer.getUpdateSelector(state, {
      id: match.params.automationRuleId,
    }),
  );

  const updatePath = useCallback(
    (pathname: string) => {
      dispatch(replace(pathname));
    },
    [
      dispatch,
    ],
  );

  const resetUpdateState = () => {
    dispatch(
      automationRulesReducer.updateAutomationRuleReset({
        id: match.params.automationRuleId,
      }),
    );
  };

  const daysValidations = useDaysValidations();

  const updateAutomationRule = (automationRuleData: AutomationRuleData) => {
    dispatch(
      automationRulesReducer.updateAutomationRule({
        id: match.params.automationRuleId,
        data: {
          days: automationRuleData.days,
          active: automationRuleData.active,
          workspaceId: match.params.id,
        },
        pipe: {
          onSuccess: () => {
            setIsEditMode(false);
          },
        },
      }),
    );
  };

  const checkEditPermission = useCallback(
    () => checkAcl(automationRule.acl, 'automation_rule:update'),
    [
      automationRule,
    ],
  );

  useEffect(() => {
    if (!isEditMode && location?.query.edit && checkEditPermission()) {
      updatePath(location.pathname);

      setIsEditMode(true);
    }
  }, [
    location,
    isEditMode,
    checkEditPermission,
    updatePath,
  ]);

  const getInitialValues = () => {
    const { active, days } = automationRule;

    return {
      active,
      days,
    };
  };

  const setFormVisible = (_isEditMode: boolean) => {
    setIsEditMode(_isEditMode);
  };

  if (!automationRule || !automationRule.id) {
    return null;
  }

  const infoPairs = [
    {
      label: (
        <T
          _str="ID"
          _comment="ID label for the details footer."
        />
      ),
      value: automationRule.id,
    },
    {
      label: (
        <T
          _str="Created"
          _comment="Creation date label for the details footer"
        />
      ),
      value: <LocalizedDateTime datetime={automationRule.createdTime} />,
    },
  ];

  return (
    <>
      <Form
        initialValues={getInitialValues()}
        onSubmit={updateAutomationRule}
        render={({ handleSubmit }) => (
          <form onSubmit={handleSubmit}>
            <EditableForm.Header>
              <EditableForm.Actions
                resetFormState={resetUpdateState}
                formState={updateState}
                isEditMode={isEditMode}
                setIsEditMode={setFormVisible}
                disabled={!checkEditPermission()}
              />
            </EditableForm.Header>
            <EditableForm.Body>
              <h2 className={style.Header}>
                <T
                  _str="Rule details"
                  _comment="Used as the title of the automation rules page"
                />
              </h2>
              <div className={style.RowActive}>
                <EditableForm.Label className={style.Label}>
                  <T
                    _str="Active"
                    _comment="Label for automation rule attribute."
                  />
                </EditableForm.Label>
                <Field
                  name="active"
                  type="checkbox"
                  render={({ input }) => (
                    <Toggle
                      version="latest"
                      disabled={!isEditMode}
                      containerClassName={style.Label}
                      isControlled
                      checked={input.checked}
                      onChange={input.onChange}
                    />
                  )}
                />
              </div>
              <div className={style.Row}>
                <EditableForm.Label>
                  <T
                    _str="Trigger after (days)"
                    _comment="Label for automation rule attribute."
                  />
                  <EditableForm.RequiredAsterisk isEditMode={isEditMode} />
                </EditableForm.Label>
                <Field
                  id="days"
                  name="days"
                  component={TextField}
                  validate={daysValidations}
                  placeholder={message({
                    id: 'Enter number of days',
                    comment:
                      'Placeholder for automation rule days on the automation rule details page.',
                  })}
                  maxLength={50}
                  required
                  autoFocus
                  disabled={!isEditMode}
                />
              </div>
            </EditableForm.Body>
          </form>
        )}
      />
      <DetailsFooter infoPairs={infoPairs} />
    </>
  );
};

type MapperProps = {
  message: MessageTranslator;
};

export default adminPage(({ props: { message } }: { props: MapperProps }) => ({
  title: message({
    id: 'Details',
    comment: 'Page title for automation rule details page.',
  }),
  modules: [
    [
      {
        path: '/details',
        title: message({
          id: 'Details',
          comment: 'Page title for automation rule details page.',
        }),
      },
    ],
  ],
}))(AutomationRuleDetails);
